import { supabase } from './supabase';
import { STORAGE_BUCKET } from './supabase';

export interface UploadedDocument {
  id: string;
  submission_id: string;
  docket_number: string;
  file_name: string;
  filing_type: string | null;
  storage_path: string;
  file_size: number | null;
  is_express_comment: boolean;
  source_url: string | null;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentUploadData {
  submission_id: string;
  docket_number: string;
  file_name: string;
  filing_type?: string | null;
  storage_path: string;
  file_size?: number | null;
  is_express_comment?: boolean;
  source_url?: string | null;
}

/**
 * Check if a document already exists in the database
 * @param submissionId - ECFS submission ID
 * @param docketNumber - Docket number
 * @param fileName - Original filename
 * @returns true if document exists, false otherwise
 */
export async function checkDocumentExists(
  submissionId: string,
  docketNumber: string,
  fileName: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('uploaded_documents')
      .select('id')
      .eq('submission_id', submissionId)
      .eq('docket_number', docketNumber)
      .eq('file_name', fileName)
      .eq('is_express_comment', false)
      .limit(1);

    if (error) {
      // Log errors but don't throw - allow upload to proceed
      console.warn('Error checking document existence:', error);
      return false;
    }

    return !!(data && data.length > 0);
  } catch (error) {
    console.error('Error checking document existence:', error);
    // On error, return false to allow upload to proceed (fail open)
    return false;
  }
}

function sanitizeForFilename(str: string): string {
  return str
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^\w\-_.]/g, '')
    .substring(0, 100);
}

function sanitizeFilingTypeForStorage(filingType?: string | null): string | null {
  if (!filingType) return null;
  return filingType
    .toString()
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .toUpperCase();
}

/**
 * Verify whether the expected uploaded object exists in Storage.
 * This is used to detect DB rows that still exist after storage objects were deleted.
 */
export async function checkDocumentExistsInStorage(
  submissionId: string,
  docketNumber: string,
  filingType?: string | null,
  originalFileName?: string
): Promise<boolean> {
  try {
    const sanitizedFilingType = sanitizeFilingTypeForStorage(filingType);
    const folderPrefix = sanitizedFilingType
      ? `ecfs/${docketNumber}/${sanitizedFilingType}`
      : `ecfs/${docketNumber}`;
    const submissionPrefix = `${submissionId}___`;
    const sanitizedTargetName = originalFileName ? sanitizeForFilename(originalFileName) : null;

    const PAGE_SIZE = 1000;
    let offset = 0;

    for (;;) {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(folderPrefix, { limit: PAGE_SIZE, offset });

      if (error) {
        console.warn('Error checking storage object existence:', error);
        return false;
      }

      const entries = data || [];
      const hasMatch = entries.some((entry) => {
        if (entry.id === null) return false; // folder placeholder
        if (!entry.name.startsWith(submissionPrefix)) return false;
        if (!sanitizedTargetName) return true;
        return entry.name.endsWith(`___${sanitizedTargetName}`);
      });

      if (hasMatch) return true;
      if (entries.length < PAGE_SIZE) return false;
      offset += PAGE_SIZE;
    }
  } catch (error) {
    console.warn('Error checking storage existence fallback:', error);
    return false;
  }
}

/**
 * Record a successful document upload in the database
 * @param documentData - Document metadata to record
 * @returns true if successful, false otherwise
 */
export async function recordDocumentUpload(
  documentData: DocumentUploadData
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('uploaded_documents')
      .insert({
        submission_id: documentData.submission_id,
        docket_number: documentData.docket_number,
        file_name: documentData.file_name,
        filing_type: documentData.filing_type || null,
        storage_path: documentData.storage_path,
        file_size: documentData.file_size || null,
        is_express_comment: documentData.is_express_comment || false,
        source_url: documentData.source_url || null,
        uploaded_at: new Date().toISOString()
      });

    if (error) {
      // If it's a unique constraint violation, that's okay - document already tracked
      if (error.code === '23505') {
        console.log('Document already tracked (unique constraint):', {
          submission_id: documentData.submission_id,
          docket_number: documentData.docket_number,
          file_name: documentData.file_name
        });
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error recording document upload:', error);
    // Don't throw - we don't want to break the upload process if tracking fails
    return false;
  }
}

/**
 * Check if an express comment already exists in the database
 * Express comments are uniquely identified by submission_id and docket_number
 * @param submissionId - ECFS submission ID
 * @param docketNumber - Docket number
 * @returns true if express comment exists, false otherwise
 */
export async function checkExpressCommentExists(
  submissionId: string,
  docketNumber: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('uploaded_documents')
      .select('id')
      .eq('submission_id', submissionId)
      .eq('docket_number', docketNumber)
      .eq('is_express_comment', true)
      .limit(1);

    if (error) {
      // Log errors but don't throw - allow upload to proceed
      console.warn('Error checking express comment existence:', error);
      return false;
    }

    return !!(data && data.length > 0);
  } catch (error) {
    console.error('Error checking express comment existence:', error);
    // On error, return false to allow upload to proceed (fail open)
    return false;
  }
}

/**
 * Record a successful express comment upload in the database
 * @param commentData - Express comment metadata to record
 * @returns true if successful, false otherwise
 */
export async function recordExpressCommentUpload(
  commentData: DocumentUploadData
): Promise<boolean> {
  try {
    // For express comments, the file_name is typically generated, so we use a consistent pattern
    // The storage path should contain the actual filename
    const fileName = commentData.file_name || `express_comment_${commentData.submission_id}.txt`;

    const { error } = await supabase
      .from('uploaded_documents')
      .insert({
        submission_id: commentData.submission_id,
        docket_number: commentData.docket_number,
        file_name: fileName,
        filing_type: commentData.filing_type || 'EXPRESS-COMMENT',
        storage_path: commentData.storage_path,
        file_size: commentData.file_size || null,
        is_express_comment: true,
        source_url: commentData.source_url || null,
        uploaded_at: new Date().toISOString()
      });

    if (error) {
      // If it's a unique constraint violation, that's okay - express comment already tracked
      if (error.code === '23505') {
        console.log('Express comment already tracked (unique constraint):', {
          submission_id: commentData.submission_id,
          docket_number: commentData.docket_number
        });
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error recording express comment upload:', error);
    // Don't throw - we don't want to break the upload process if tracking fails
    return false;
  }
}

/**
 * Batch check multiple documents to see which ones already exist
 * @param documents - Array of document identifiers to check
 * @returns Map of document keys to boolean (true if exists)
 */
export async function batchCheckDocuments(
  documents: Array<{ submission_id: string; docket_number: string; file_name: string }>
): Promise<Map<string, boolean>> {
  const result = new Map<string, boolean>();

  if (documents.length === 0) {
    return result;
  }

  try {
    // Build query to check all documents at once
    const submissionIds = [...new Set(documents.map(d => d.submission_id))];
    const docketNumbers = [...new Set(documents.map(d => d.docket_number))];

    const { data, error } = await supabase
      .from('uploaded_documents')
      .select('submission_id, docket_number, file_name')
      .in('submission_id', submissionIds)
      .in('docket_number', docketNumbers)
      .eq('is_express_comment', false);

    if (error) {
      console.warn('Error batch checking documents:', error);
      // Return all as false (fail open)
      documents.forEach(doc => {
        const key = `${doc.submission_id}|${doc.docket_number}|${doc.file_name}`;
        result.set(key, false);
      });
      return result;
    }

    // Create a set of existing documents for fast lookup
    const existingDocs = new Set<string>();
    (data || []).forEach(doc => {
      const key = `${doc.submission_id}|${doc.docket_number}|${doc.file_name}`;
      existingDocs.add(key);
    });

    // Check each requested document
    documents.forEach(doc => {
      const key = `${doc.submission_id}|${doc.docket_number}|${doc.file_name}`;
      result.set(key, existingDocs.has(key));
    });

    return result;
  } catch (error) {
    console.error('Error batch checking documents:', error);
    // Return all as false (fail open)
    documents.forEach(doc => {
      const key = `${doc.submission_id}|${doc.docket_number}|${doc.file_name}`;
      result.set(key, false);
    });
    return result;
  }
}

